import { and, eq, inArray } from 'drizzle-orm'
import { db as defaultDb } from '@/db'
import { aiArtifactTranslations, ticketMessageTranslations, ticketTranslations } from '@/db/schema'
import { hashContent } from '@/lib/kb-translation'
import type { SupportedLanguage } from '@/lib/user-language'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type TranslatableTicketMessage = {
  id: string
  language: SupportedLanguage
  bodyRaw: string
  bodySanitized: string
}

export type TranslatableAiArtifact = {
  id: string
  language: SupportedLanguage
  content: string
}

export type TranslatableTicket = {
  id: string
  title: string | null
  department: string | null
  issueCategory: string | null
  affectedSystem: string | null
  successCriteria: string | null
  actionRequested: string | null
}

export function hashTicketDisplayContent(ticket: TranslatableTicket): string {
  return hashContent([
    ticket.title ?? '',
    ticket.department ?? '',
    ticket.issueCategory ?? '',
    ticket.affectedSystem ?? '',
    ticket.successCriteria ?? '',
    ticket.actionRequested ?? '',
  ].join('\n---\n'))
}

export async function applyTicketTranslation<T extends TranslatableTicket | null | undefined>(
  ticket: T,
  viewerLanguage: SupportedLanguage,
  db: DB = defaultDb as DB,
): Promise<T> {
  if (!ticket || viewerLanguage === 'en') return ticket

  const [translation] = await db
    .select()
    .from(ticketTranslations)
    .where(
      and(
        eq(ticketTranslations.ticketId, ticket.id),
        eq(ticketTranslations.language, viewerLanguage),
        eq(ticketTranslations.reviewStatus, 'approved'),
      ),
    )
    .limit(1)

  if (!translation || translation.sourceContentHash !== hashTicketDisplayContent(ticket)) return ticket

  return {
    ...ticket,
    title: translation.title ?? ticket.title,
    department: translation.department ?? ticket.department,
    issueCategory: translation.issueCategory ?? ticket.issueCategory,
    affectedSystem: translation.affectedSystem ?? ticket.affectedSystem,
    successCriteria: translation.successCriteria ?? ticket.successCriteria,
    actionRequested: translation.actionRequested ?? ticket.actionRequested,
  } as T
}

export async function applyTicketTranslations<T extends TranslatableTicket>(
  tickets: T[],
  viewerLanguage: SupportedLanguage,
  db: DB = defaultDb as DB,
): Promise<T[]> {
  if (viewerLanguage === 'en' || tickets.length === 0) return tickets

  const translations = await db
    .select()
    .from(ticketTranslations)
    .where(
      and(
        eq(ticketTranslations.language, viewerLanguage),
        eq(ticketTranslations.reviewStatus, 'approved'),
        inArray(ticketTranslations.ticketId, tickets.map((ticket) => ticket.id)),
      ),
    )

  const translationsByTicketId = new Map(translations.map((translation) => [translation.ticketId, translation]))

  return tickets.map((ticket) => {
    const translation = translationsByTicketId.get(ticket.id)
    if (!translation || translation.sourceContentHash !== hashTicketDisplayContent(ticket)) return ticket

    return {
      ...ticket,
      title: translation.title ?? ticket.title,
      department: translation.department ?? ticket.department,
      issueCategory: translation.issueCategory ?? ticket.issueCategory,
      affectedSystem: translation.affectedSystem ?? ticket.affectedSystem,
      successCriteria: translation.successCriteria ?? ticket.successCriteria,
      actionRequested: translation.actionRequested ?? ticket.actionRequested,
    }
  })
}

export async function applyTicketMessageTranslations<T extends TranslatableTicketMessage>(
  messages: T[],
  viewerLanguage: SupportedLanguage,
  db: DB = defaultDb as DB,
): Promise<T[]> {
  const sourceMessages = messages.filter((message) => message.language !== viewerLanguage)
  if (sourceMessages.length === 0) return messages

  const translations = await db
    .select()
    .from(ticketMessageTranslations)
    .where(
      and(
        eq(ticketMessageTranslations.language, viewerLanguage),
        eq(ticketMessageTranslations.reviewStatus, 'approved'),
        inArray(ticketMessageTranslations.messageId, sourceMessages.map((message) => message.id)),
      ),
    )

  const translationsByMessageId = new Map(translations.map((translation) => [translation.messageId, translation]))

  return messages.map((message) => {
    if (message.language === viewerLanguage) return message

    const translation = translationsByMessageId.get(message.id)
    if (!translation || translation.sourceBodyHash !== hashContent(message.bodyRaw)) return message

    return {
      ...message,
      language: viewerLanguage,
      bodyRaw: translation.body,
      bodySanitized: translation.body,
    }
  })
}

export async function applyAiArtifactTranslation<T extends TranslatableAiArtifact | null | undefined>(
  artifact: T,
  viewerLanguage: SupportedLanguage,
  db: DB = defaultDb as DB,
): Promise<T> {
  if (!artifact || artifact.language === viewerLanguage) return artifact

  const [translation] = await db
    .select()
    .from(aiArtifactTranslations)
    .where(
      and(
        eq(aiArtifactTranslations.artifactId, artifact.id),
        eq(aiArtifactTranslations.language, viewerLanguage),
        eq(aiArtifactTranslations.reviewStatus, 'approved'),
      ),
    )
    .limit(1)

  if (!translation || translation.sourceContentHash !== hashContent(artifact.content)) return artifact

  return {
    ...artifact,
    language: viewerLanguage,
    content: translation.content,
  } as T
}
